import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Pill from '../Pill/Pill';
import TagInput from './TagInput';

/**
 * Tag cloud with an optional constrained tag input. When the Tag Manager
 * feature is enabled, additions must exactly match (case-insensitively) one
 * of the active tags; otherwise free text is accepted. Removal of already
 * selected tags is controlled only by the existing `disabled` prop.
 */
export class TagCloudWithoutRedux extends Component {
  sortTags = tags => {
    return tags?.slice().sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  };

  /**
   * Returns active tags that are not already selected.
   * Comparison is case-insensitive.
   * @param {string[]} activeTags
   * @param {string[]} selectedTags
   * @return {string[]} active tags, not currently selected
   */
  getAvailableTags = (activeTags, selectedTags) => {
    activeTags = activeTags || [];
    selectedTags = selectedTags || [];

    return activeTags.filter(
      tag => !selectedTags.some(selected => selected.toLowerCase() === tag.toLowerCase()),
    );
  };

  state = { tags: this.sortTags(this.props.tags || []) };

  // tags should be stored in a sorted manner
  setTags = tags => {
    const sortedTags = this.sortTags(tags || []);
    const { tags: stateTags } = this.state;

    if (
      sortedTags.length !== stateTags.length ||
      sortedTags.some((tag, index) => tag !== stateTags[index])
    ) {
      this.setState({ tags: sortedTags });
    }
  };

  componentDidUpdate(prevProps) {
    const { tags } = this.props;

    if (prevProps.tags !== tags) {
      this.setTags(tags);
    }
  }

  onChange = tags => {
    const { onChange } = this.props;

    onChange(this.sortTags(tags));
  };

  onAdd = newTagValue => {
    this.addTag(newTagValue);
    this.input.value = null;
    this.input.focus();
  };

  addTag = name => {
    const { tags: stateTags } = this.state;
    const tags = stateTags || [];

    if (name.trim() === '') {
      return false;
    }

    const tagClone = tags.slice(0);
    // Does the tag exist? If so we return and don't add the tag
    const found = tags.find(tag => tag.toString().toLowerCase() === name.toString().toLowerCase());

    if (found) {
      return false;
    }

    tagClone.push(name.trim());
    this.setTags(tagClone);
    this.onChange(tagClone);

    return true;
  };

  renderTags = () => {
    const { disabled } = this.props;
    const { tags } = this.state;
    const tagNodes = [];

    if (!tags) {
      return null;
    }

    tags.forEach((tag, index) => {
      const handler = () => {
        const tagClone = tags.slice(0);

        tagClone.splice(index, 1);
        this.setTags(tagClone);
        this.onChange(tagClone);
      };

      tagNodes.push(
        <Pill key={index} handler={handler} disabled={disabled}>
          {tag}
        </Pill>,
      );
    });

    return tagNodes;
  };

  render() {
    const {
      disabled,
      hideTagsText = false,
      activeTags,
      tagmanagerEnabled,
      tagManagerInputDisabled,
    } = this.props;
    const { tags: stateTags } = this.state;
    // The parent `disabled` prop disables the whole control, including the
    // input and Add button, on top of any Tag Manager request state.
    const inputDisabled = disabled || tagManagerInputDisabled;
    // Filter activeTags to exclude already selected tags
    const availableTags = this.getAvailableTags(activeTags, stateTags);

    return (
      <div className="space-y-4 group" data-test="tagcloud">
        {!hideTagsText && (
          <label
            htmlFor="input-tag"
            className="cursor-pointer text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 ml-2 group-focus-within:text-warmth"
          >
            Tags
          </label>
        )}
        <div className="flex flex-wrap gap-2 p-2 min-h-[60px] border-b border-gray-100 focus-within:border-warmth transition-colors">
          <TagInput
            useList={tagmanagerEnabled}
            options={availableTags}
            onAdd={this.onAdd}
            inputRef={ref => {
              this.input = ref;
            }}
            ref={ref => {
              this.tagInput = ref;
            }}
            disabled={inputDisabled}
          />

          <button
            type="button"
            data-test="button-addtag"
            onClick={() => this.tagInput?.handleAdd()}
            disabled={inputDisabled}
            className="flex items-center gap-2 bg-dark-night text-white px-4 py-1.5 rounded-full text-xs font-bold group"
          >
            <span className="text-inherit">Add</span>
          </button>
        </div>
        <div className="tags block">{this.renderTags()}</div>
      </div>
    );
  }
}

TagCloudWithoutRedux.propTypes = {
  tags: PropTypes.array,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  hideTagsText: PropTypes.bool,
  activeTags: PropTypes.arrayOf(PropTypes.string),
  tagmanagerEnabled: PropTypes.bool,
  tagManagerInputDisabled: PropTypes.bool,
};

TagCloudWithoutRedux.defaultProps = {
  tags: [],
  activeTags: [],
  tagmanagerEnabled: false,
  tagManagerInputDisabled: false,
};

const mapStateToProps = state => {
  const { tags, enabled, tagsStatus, configStatus } = state.tagManager;
  const activeTags = tags
    .map(row => row.tag)
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  // Only treat the feature as enabled after the config request succeeds; the
  // request status distinguishes a loaded `false` from an unknown setting.
  const tagmanagerEnabled = configStatus === 'loaded' && enabled;
  // Disable only the input and Add button while requests are pending, when
  // the config request failed (the flag is unknown), or when the feature is
  // enabled but its active-tag request failed. Removal of selected tags is
  // unaffected; the existing `disabled` prop still disables everything.
  const requestsPending =
    tagsStatus === 'idle' ||
    tagsStatus === 'loading' ||
    configStatus === 'idle' ||
    configStatus === 'loading';
  const tagManagerInputDisabled =
    requestsPending || configStatus === 'failed' || (tagmanagerEnabled && tagsStatus !== 'loaded');

  return { activeTags, tagmanagerEnabled, tagManagerInputDisabled };
};

export default connect(mapStateToProps)(TagCloudWithoutRedux);
